<template>
  <el-row class="pedido-row">
    <!-- CHECKBOX (2 FILAS) -->
    <el-col
      :span="1"
      class="cell side-cell"
    >
      <el-popover
        :disabled="!checkboxDisabled"
        placement="top-start"
        :width="400"
        trigger="hover"
        :content="checkboxDisabledReason"
      >
        <template #reference>
          <el-checkbox
            :disabled="checkboxDisabled"
            @change="changeCheckbox"
          />
        </template>
      </el-popover>
    </el-col>

    <!-- CONTENIDO -->
    <el-col :span="20">
      <!-- FILA 1 -->
      <el-row
        class="fila"
        align="middle"
      >
        <el-col
          :span="2"
          class="cell"
        >
          <label>Cod Pedido</label>
          <div>{{ item.codigoPedido }}</div>
        </el-col>

        <el-col
          :span="3"
          class="cell"
        >
          <label>N° Orden Compra</label>
          <div>{{ item.ordenCompra }}</div>
        </el-col>

        <el-col
          :span="2"
          class="cell"
        >
          <label>Fecha Pedido</label>
          <div>{{ item.fechaAlta }}</div>
        </el-col>

        <el-col
          :span="3"
          class="cell"
        >
          <label>Fecha Entrega</label>
          <div>{{ item.fechaEntrega }}</div>
        </el-col>

        <el-col
          :span="2"
          class="cell"
        >
          <label>Cantidad</label>
          <div>{{ item.cantidad }}</div>
        </el-col>

        <el-col
          :span="2"
          class="cell"
        >
          <label>Cant. Pendiente</label>
          <div>{{ item.cantidadSaldo }}</div>
        </el-col>

        <el-col
          :span="3"
          class="cell"
        >
          <label>Unidad Medida</label>
          <div>{{ item.unidadMedida }}</div>
        </el-col>
        <el-col
          :span="2"
          class="cell"
        >
          <label>Moneda</label>
          <div>{{ item.monedaPedido }}</div>
        </el-col>
        <el-col
          :span="4"
          class="cell nowrap"
        >
          <label>Tipo Producto</label>
          <div>{{ item.ElementoGrupo.Descripcion }}</div>
        </el-col>
      </el-row>

      <!-- FILA 2 -->
      <el-row
        class="fila fila-secundaria"
        align="middle"
      >
        <el-col
          :span="2"
          class="cell"
        >
          <label>Estado</label>
          <el-tag
            size="small"
            :type="estadoTag"
          >
            {{ item.estado }}
          </el-tag>
        </el-col>

        <el-col
          :span="5"
          class="cell"
        >
          <label>Cliente</label>
          <div>{{ item.descripcionCliente }}</div>
        </el-col>

        <el-col
          :span="3"
          class="cell"
        >
          <label>Vendedor</label>
          <div>{{ item.descripcionVendedor }}</div>
        </el-col>

        <el-col
          :span="2"
          class="cell"
        >
          <label>Cod. Producto</label>
          <div>{{ item.codigoProductoPedido }}</div>
        </el-col>

        <el-col
          :span="9"
          class="cell nowrap"
        >
          <label>Producto</label>
          <div>{{ item.descripcionProducto }}</div>
        </el-col>
      </el-row>
    </el-col>
  </el-row>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useOrderImportStore } from '@/views/order_import/stores/order_import_store';
import { initialProspect } from '@/views/prospects/models/prospect_model';
import { initialUsers } from '@/views/users/models/user_model';
import { Material } from '@/views/material/models/material_model';
import unidad_medida from '@/shared/models/unidad_medida_model';
import moneda from '@/shared/models/moneda_model';

const orderImportStore = useOrderImportStore();
const { selectedItems } = storeToRefs(orderImportStore);

const emit = defineEmits(['check-selected']);

const reasons = ref([]);

const props = defineProps({
  item: { type: Object, default: () => {} },
});

const estadoTag = computed(() => {
  switch (props.item.estado) {
    case 'Pendiente':
      return 'success';
    case 'Completado':
      return 'info';
    case 'Anulado':
      return 'danger';
    default:
      return '';
  }
});

watch(
  [() => props.item, selectedItems],
  () => {
    reasons.value = [];
    if (props.item.estado !== 'Pendiente' || props.item.cantidadSaldo <= 0) {
      reasons.value.push('* El pedido no está pendiente o no tiene cantidad saldo');
    }
    if (selectedItems.value.length > 0) {
      if (props.item.tipoProducto !== selectedItems.value[0].tipoProducto) {
        reasons.value.push('* El tipo de producto no coincide con otros pedidos seleccionados');
      }
      if (props.item.unidadMedida !== selectedItems.value[0].unidadMedida) {
        reasons.value.push('* La unidad de medida no coincide con otros pedidos seleccionados');
      }
      if (props.item.monedaPedido !== selectedItems.value[0].monedaPedido) {
        reasons.value.push('* La moneda no coincide con otros pedidos seleccionados');
      }
    }
  },
  { immediate: true },
);

const checkboxDisabledReason = computed(() => reasons.value.join(', '));

const checkboxDisabled = computed(() => reasons.value.length > 0);

const changeCheckbox = (value) => {
  if (!value) {
    orderImportStore.checkSelected({ ...props.item, Event: 'remove' });
    return;
  }
  const order = JSON.parse(JSON.stringify(props.item));
  if (!order.Cliente) {
    order.Cliente = { ...initialProspect };
  }
  if (!order.Vendedor) {
    order.Vendedor = { ...initialUsers };
  }
  if (!order.Producto) {
    order.Producto = { ...Material };
  }
  if (!order.UnidadVenta) {
    order.UnidadVenta = { ...unidad_medida };
  }
  if (!order.Moneda) {
    order.Moneda = { ...moneda, Codigo: null };
  }
  orderImportStore.checkSelected({ ...order, Event: 'add', cantidad: order.cantidadSaldo });
};
</script>

<style scoped>
.pedido-row {
  border: 1px solid var(--el-border-color-light);
  border-radius: 6px;
  margin-bottom: 8px;
}

:deep(.el-checkbox__input.is-disabled .el-checkbox__inner) {
  --el-checkbox-disabled-border-color: #f0f0f0;
}

.fila {
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.fila-secundaria {
  background: var(--el-fill-color-light);
}

.cell {
  padding: 8px;
  font-size: 13px;
}

.cell label {
  display: block;
  font-size: 11px;
  color: var(--el-text-color-secondary);
  margin-bottom: 2px;
}

.side-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nowrap {
  white-space: nowrap;
}
</style>
